import cn from 'classnames';
import { useMemo, useCallback, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from "react-query";
import i18n from "i18next";
import { Filters, FilterTypes } from '@containers/FiltersNew';
import { Title2 } from "@components/new/Typo";
import { Button, ButtonSize } from "@components/new/Button";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import { Alert, AlertType } from "@components/new/Alert";
import DataTable from '@containers/DataTableNew';
import Photo from "@components/new/Photo";
import { TableCellLink, TableColumn } from "@components/new/Table";
import { MarketplaceStoreGroup } from "@components/new/MarketplaceStoreGroup";
import { Marketplace } from "@components/new/Marketplace";
import { FilterModel } from '@hooks/useFiltersSearchParams';
// import { NoData } from "@components/new/NoData";
import {
  getFBPStocks,
  isFBPStocksNewVersion,
  getFBPLowStockCount,
  useFBPStocksCounter,
  TWbItem,
  TYmItem,
  TAliItem,
  TRequestParams,
  TFBPStockItem,
  TSellerWarehouse,
} from "@api/apiClient/fbp";
import { weightGValue, formatNumber } from "@utils/formatValues";
import { useKnowledgeBase } from '@contexts/KnowledgeBaseContext';
import InfoIcon from '@assets/icons/outline/info.svg?react';
import IconPlus from '@assets/icons/outline/plus.svg?react';
import IconLink from '@assets/icons/outline/git-compare.svg?react';
import ChevronDown from "@assets/icons/outline/chevron-down.svg?react";
import ChevronUp from "@assets/icons/outline/chevron-up.svg?react";
import styles from './Tab.module.scss';

type TMatchingItem = {
  wbProduct?: TWbItem;
  ymProduct?: TYmItem;
  aliProduct?: TAliItem;
  present: number;
}

const isGroup = (data: TFBPStockItem) => (((data.wbItems?.length ?? 0) + (data.ymItems?.length ?? 0) + (data.aliItems?.length ?? 0)) > 1);
const getNestedData = (data: TFBPStockItem): TMatchingItem[] => [...data.wbItems, ...data.ymItems, ...data.aliItems];
const lowerFirstLetter = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);
const LOW_STOCK_PRESENT_LIMIT = 10;
const isLowStockPresent = (present: number) => present < LOW_STOCK_PRESENT_LIMIT;

const AvailabilityTab = ({ sellerWarehouse, openCreateOrderModal }: {
  sellerWarehouse: TSellerWarehouse | undefined;
  openCreateOrderModal: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const sellerWarehouseId = sellerWarehouse?.id ?? "";
  const currentLanguage = i18n.language;
  const [sorting, setSorting] = useState<{ column: string, direction: string } | undefined>(undefined);
  // const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const { data: stocksCounter } = useFBPStocksCounter(sellerWarehouseId ?? "");
  const { data: lowStockCounter } = useQuery({
    queryKey: ["getFBPLowStockCount", sellerWarehouseId],
    queryFn: () => getFBPLowStockCount(sellerWarehouseId),
    retry: 1,
    enabled: Boolean(sellerWarehouseId),
  });
  const { setKnowledgeBaseParams } = useKnowledgeBase();
  const outOfStockCount = lowStockCounter?.outOfStockCount ?? 0;
  const lowStockCount = lowStockCounter?.lowStockCount ?? 0;
  const hasLowStockAlert = outOfStockCount > 0 || lowStockCount > 0;
  const outOfStockAlertDescription = t("fbpPage.outOfStockAlertDescription", {
    count: outOfStockCount,
    outOfStockCount,
  });
  const lowStockAlertDescription = t("fbpPage.lowStockAlertDescription", {
    count: lowStockCount,
  });
  const stockAlertDescription = [
    outOfStockCount > 0
      ? `${outOfStockAlertDescription}${lowStockCount > 0 ? "," : ""}`
      : "",
    lowStockCount > 0
      ? (outOfStockCount > 0 ? lowerFirstLetter(lowStockAlertDescription) : lowStockAlertDescription)
      : "",
  ].filter(Boolean).join(" ");

  const stocksGetter = useCallback(
    ({ filters, sorting, page }: TRequestParams) => getFBPStocks({
      filters,
      sorting,
      page,
      sellerWarehouseId,
      includeNonPositivePresent: true,
    }),
    [sellerWarehouseId]
  );

  const columns: TableColumn<TFBPStockItem>[] = useMemo(() => [
    {
      header: "",
      renderCell: (row) => (
        (isGroup(row.data) ? (
          row.isOpen ? (
            <ChevronUp
              className={styles.chevron}
              onClick={() => (row.toggleIsOpen && row.toggleIsOpen())}
            />
          ) : (
            <ChevronDown
              className={styles.chevron}
              onClick={() => (row.toggleIsOpen && row.toggleIsOpen())}
            />
          )
        ) : null)
      ),
      width: "38px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.stocksTab.columns.photo"),
      renderCell: (row) => (
        (isGroup(row.data) ? (
          <IconLink className={styles.linkIcon}/>
        ) : (
          <div className={styles.tableNameColumn}>
            {row.data.wbItems?.[0] ? (
              <Photo src={row.data.wbItems?.[0].wbProduct.primaryImage} />
            ) : null}
            {row.data.ymItems?.[0] ? (
              <Photo src={row.data.ymItems?.[0].ymProduct.primaryImage} />
            ) : null}
            {row.data.aliItems?.[0] ? (
              <Photo src={row.data.aliItems?.[0].aliProduct.primaryImage} />
            ) : null}
          </div>
        ))
      ),
      width: "80px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.stocksTab.columns.name"),
      renderCell: (row) => {
        const wbItem = row.data.wbItems?.[0];
        const ymItem = row.data.ymItems?.[0];
        const aliItem = row.data.aliItems?.[0];
        return (
          (isGroup(row.data) ? (
            <div className={styles.matchingRowName}>
              <MarketplaceStoreGroup>
                {wbItem ? <Marketplace name="Wildberries" /> : null}
                {ymItem ? <Marketplace name="Yandex" /> : null}
                {aliItem ? <Marketplace name="Aliexpress" /> : null}
              </MarketplaceStoreGroup>
              <Title2 ellipsis lines={2}>{row.data.matchingLinkName ?? ""}</Title2>
            </div>
          ) : (
            <div className={styles.tableNameColumn}>
              {wbItem ? (
                <div className={styles.nameColumnRow}>
                  <Marketplace name="Wildberries" size="small" />
                  <TableCellLink
                    title={wbItem.wbProduct.name}
                    description={wbItem.wbProduct.wbNmId}
                    to={wbItem.wbProduct.url}
                    external
                  />
                </div>
              ) : null}
              {ymItem ? (
                <div className={styles.nameColumnRow}>
                  <Marketplace name="Yandex" size="small" />
                  <TableCellLink
                    title={ymItem.ymProduct.name}
                    description={ymItem.ymProduct.sku}
                    to={ymItem.ymProduct.url}
                    external
                  />
                </div>
              ) : null}
              {aliItem ? (
                <div className={styles.nameColumnRow}>
                  <Marketplace name="Aliexpress" size="small" />
                  <TableCellLink
                    title={aliItem.aliProduct.name}
                    description={aliItem.aliProduct.skuCode}
                    to={aliItem.aliProduct.url}
                    external
                  />
                </div>
              ) : null}
            </div>
          ))
        )
      },
      width: "200px",
    },
    {
      header: t("fbpPage.stocksTab.columns.size"),
      renderCell: (row) => {
        const wbItem = row.data.wbItems?.[0];
        return (isGroup(row.data) ? (
          null
        ) : (
          wbItem?.wbProduct?.wbTechSize ?? ""
        ));
      },
      width: "160px",
      fixWidth: true,
      alignment: "center",
    },
    {
      id: "Present",
      header: t("fbpPage.stocksTab.columns.count"),
      renderCell: (row) => {
        const wbItem = row.data.wbItems?.[0];
        const ymItem = row.data.ymItems?.[0];
        const aliItem = row.data.aliItems?.[0];
        if (isGroup(row.data)) {
          const value = (
            row.data.ymItems.reduce((acc, v) => (acc + (v?.present ?? 0)), 0)
            + row.data.wbItems.reduce((acc, v) => (acc + (v?.present ?? 0)), 0)
            + row.data.aliItems.reduce((acc, v) => (acc + (v?.present ?? 0)), 0)
          );
          return (
            <span className={cn({ [styles.lowStockCount]: isLowStockPresent(value) })}>
              {formatNumber(value)}
            </span>
          );
        }
        return (
          <div className={styles.tableColumn}>
            {wbItem ? (
              <div className={cn(styles.tableCell, { [styles.lowStockCount]: isLowStockPresent(wbItem.present) })}>
                {formatNumber(wbItem.present)}
              </div>
            ) : null}
            {ymItem ? (
              <div className={cn(styles.tableCell, { [styles.lowStockCount]: isLowStockPresent(ymItem.present) })}>
                {formatNumber(ymItem.present)}
              </div>
            ) : null}
            {aliItem ? (
              <div className={cn(styles.tableCell, { [styles.lowStockCount]: isLowStockPresent(aliItem.present) })}>
                {formatNumber(aliItem.present)}
              </div>
            ) : null}
          </div>
        );
      },
      width: "100px",
      fixWidth: true,
      alignment: "center",
      sortable: true,
    },
    {
      id: "Weight",
      header: t("fbpPage.stocksTab.columns.weightTotal"),
      valueGetter: (data: TFBPStockItem) => {
        const value = (
          data.ymItems.reduce((acc, v) => (acc + weightGValue(v?.ymProduct?.weight) * (v?.present ?? 0)), 0)
          + data.wbItems.reduce((acc, v) => (acc + weightGValue(v?.wbProduct?.weight) * (v?.present ?? 0)), 0)
          + data.aliItems.reduce((acc, v) => (acc + weightGValue(v?.aliProduct?.weight) * (v?.present ?? 0)), 0)
        ) / 1000;
        return value ? formatNumber(value) : "0";
      },
      alignment: "center",
      width: "90px",
      fixWidth: true,
      sortable: true,
    },
  ], [currentLanguage]);

  const nestedColumns: TableColumn<TMatchingItem>[] = useMemo(() => [
    {
      header: "",
      renderCell: () => null,
      width: "38px",
      fixWidth: true,
    },
    {
      header: "",
      renderCell: (row) => (
        <div className={styles.tableNameColumn}>
          <Photo src={row.data?.wbProduct?.primaryImage || row.data?.ymProduct?.primaryImage || row.data?.aliProduct?.primaryImage || ""} />
        </div>
      ),
      width: "80px",
      fixWidth: true,
    },
    {
      header: "",
      renderCell: (row) => {
        return (
          <div className={styles.tableNameColumn}>
            {row.data?.wbProduct ? (
              <div className={styles.nameColumnRow}>
                <Marketplace name="Wildberries" size="small" />
                <TableCellLink
                  title={row.data?.wbProduct?.name}
                  description={row.data?.wbProduct?.wbNmId}
                  to={row.data?.wbProduct?.url}
                  external
                />
              </div>
            ) : null}
            {row.data?.ymProduct ? (
              <div className={styles.nameColumnRow}>
                <Marketplace name="Yandex" size="small" />
                <TableCellLink
                  title={row.data?.ymProduct?.name}
                  description={row.data?.ymProduct?.sku}
                  to={row.data?.ymProduct?.url}
                  external
                />
              </div>
            ) : null}
            {row.data?.aliProduct ? (
              <div className={styles.nameColumnRow}>
                <Marketplace name="Aliexpress" size="small" />
                <TableCellLink
                  title={row.data?.aliProduct?.name}
                  description={row.data?.aliProduct?.skuCode}
                  to={row.data?.aliProduct?.url}
                  external
                />
              </div>
            ) : null}
          </div>
        )
      },
      width: "200px",
    },
    {
      header: "",
      renderCell: (row) => {
        return (
          <div className={styles.tableColumn}>
            {row.data?.wbProduct ? (
              <div className={styles.tableCell}>
                {row.data?.wbProduct?.wbTechSize ?? ""}
              </div>
            ) : (<div className={styles.tableCell} />)}
          </div>
        );
      },
      width: "160px",
      fixWidth: true,
      alignment: "center",
    },
    {
      header: "",
      renderCell: (row) => (
        <span className={cn({ [styles.lowStockCount]: isLowStockPresent(row.data.present) })}>
          {formatNumber(row.data.present)}
        </span>
      ),
      width: "100px",
      fixWidth: true,
      alignment: "center",
    },
    {
      id: "weight",
      header: t("fbpPage.stocksTab.columns.weightTotal"),
      valueGetter: (data: TMatchingItem) => {
        const value = (
          weightGValue(data?.ymProduct?.weight || data?.wbProduct?.weight || data?.aliProduct?.weight) * (data?.present ?? 0) / 1000
        );
        return value ? formatNumber(value) : "0";
      },
      alignment: "center",
      width: "90px",
      fixWidth: true,
      // sortable: true,
    },
  ], [currentLanguage]);

  return (
    <div className={styles.tabContent}>
      <div className={styles.actionsRow}>
        <Filters
          id="fbp-available"
          // openFilters={() => setIsFilterOpen(true)}
          items={[
            {
              name: 'skus',
              type: FilterTypes.MULTI_SEARCH_NEW,
              placeholder: t('fbpPage.itemSearch'),
            },
          ]}
          onChange={(v) => {
            setInnerFilters(v);
          }}
          values={innerFilters}
          // asPanel
        />

        <div className={styles.actionsRight}>
          <LinkButton
            icon={InfoIcon}
            size={LinkButtonSize.L}
            title={t('fbpPage.instructions')}
            onClick={() => setKnowledgeBaseParams(`https://unitrade-global.com/page139704916.html`)}
          />
          <Button
            size={ButtonSize.M}
            onClick={openCreateOrderModal}
            title={t('fbpPage.addOrderBtn')}
            icon={IconPlus}
            disabled={sellerWarehouse?.isAwaitingDeletion}
          />
        </div>
      </div>
      {hasLowStockAlert ? (
        <Alert
          type={AlertType.WARNING}
          title={t(outOfStockCount > 0 ? "fbpPage.outOfStockAlertTitle" : "fbpPage.lowStockAlertTitle")}
          description={stockAlertDescription}
          isWide
        />
      ) : null}

      <DataTable<TFBPStockItem, void, void, TMatchingItem>
        key={sellerWarehouseId}
        columns={columns}
        nestedColumns={nestedColumns}
        // defaultPageSize={10}
        filters={innerFilters}
        queryKey={["getFBPStocks", sellerWarehouseId, "includeNonPositivePresent", String(isFBPStocksNewVersion())]}
        getData={stocksGetter}
        // rowOnClick={(row) => {
        //   setCurrentRequestId(row.id)
        //   setOpenOrderModal(true);
        // }}
        verticalAlignment="center"
        noLoaderOnRefresh
        noDataProps={stocksCounter ? undefined : {
          title: t("fbpPage.noItemsTitle"),
          btnTitle: t('fbpPage.noItemsBtn'),
          onClick: openCreateOrderModal,
          width: "max-content",
        }}
        pending={!sellerWarehouseId}
        sorting={sorting}
        onSortingChange={(column, direction) => setSorting({ column, direction })}
        isRowCollapsible={isGroup}
        getNestedData={getNestedData}
      />
    </div>
  );
};

export default AvailabilityTab;
